import { calculatorsById } from '../calculators/index';
import type { Inputs } from '../calculators/types';
import { getLocale, type LocaleCode, tKey } from '../i18n/index';

/** Shown in an output slot before a valid calculation exists. */
const PLACEHOLDER = '—';

/**
 * Parse what someone actually typed into a number.
 *
 * Hungarian writes `12,5` where English writes `12.5`, and the results on this site are
 * formatted the same way — so on the Hungarian pages a comma is exactly what people read,
 * copy and then type back in. `<input type="number">` cannot accept both: it parses using
 * one convention picked by the *browser's* locale, not the page's, and reports anything
 * else as an empty value. The field would simply go blank as the comma was typed, with no
 * error to explain it. That is why the fields are plain text and are parsed here.
 *
 * Both separators are accepted in both languages, deliberately. A Hungarian distiller with
 * a numeric keypad types a full stop out of habit as readily as a comma, and refusing
 * either one would just be the same bug wearing the other hat.
 */
export function parseDecimal(raw: string): number {
  // Whitespace only ever groups thousands here — `hu-HU` formats 12 345,6 with a non-break
  // space, which is what someone pasting a result back in would bring with them. `\s`
  // covers the non-break and narrow non-break forms as well as a plain space.
  const cleaned = raw.replace(/\s/g, '');
  if (cleaned === '') return Number.NaN;

  const lastComma = cleaned.lastIndexOf(',');
  const lastDot = cleaned.lastIndexOf('.');

  let normalised: string;
  if (lastComma >= 0 && lastDot >= 0) {
    // Both present, so one of them groups thousands: the rightmost is the decimal point,
    // whichever it is, and the other can be dropped. `1.234,5` and `1,234.5` both work.
    normalised =
      lastComma > lastDot
        ? cleaned.replace(/\./g, '').replace(',', '.')
        : cleaned.replace(/,/g, '');
  } else {
    // A lone separator is a decimal point. Reading `1,234` as one thousand two hundred and
    // thirty-four would require knowing which convention the typist had in mind, and nobody
    // types thousands separators into a field asking for litres.
    normalised = cleaned.replace(',', '.');
  }

  // Hex, exponents and stray letters are mistakes rather than numbers to guess at; `Number`
  // would happily turn `0x10` into 16. NaN here becomes a normal `error.notANumber`.
  return /^[+-]?(\d+(\.\d*)?|\.\d+)$/.test(normalised) ? Number(normalised) : Number.NaN;
}

/**
 * Attach behaviour to a calculator that the build has already rendered.
 *
 * This does not create any markup — `calculatorMarkup.ts` did that at build time, so the
 * form is present and labelled before this runs. All that happens here is reading the
 * inputs, calling the pure function, and writing the numbers back.
 */
export function mountCalculator(root: HTMLElement, locale: LocaleCode): void {
  const id = root.dataset.calculator;
  const calc = id ? calculatorsById.get(id) : undefined;
  if (!calc) {
    console.warn(`No calculator registered for id "${id ?? '(missing)'}"`);
    return;
  }

  const form = root.querySelector<HTMLFormElement>('[data-calc-form]');
  const errorEl = root.querySelector<HTMLElement>('[data-calc-error]');
  if (!form || !errorEl) {
    console.warn(`Calculator "${calc.id}" is missing its form or error element`);
    return;
  }

  const inputs = new Map<string, HTMLInputElement>();
  for (const field of calc.fields) {
    const input = form.querySelector<HTMLInputElement>(`input[name="${field.name}"]`);
    if (input) inputs.set(field.name, input);
  }

  const outputs = new Map<string, HTMLElement>();
  for (const output of calc.outputs) {
    const el = root.querySelector<HTMLElement>(`[data-output="${output.name}"]`);
    if (el) outputs.set(output.name, el);
  }

  const format = numberFormatter(locale);

  const recompute = (): void => {
    const values: Inputs = {};
    // `parseDecimal`, not `valueAsNumber`: these are text fields precisely so that a comma
    // survives long enough to be read. See the note on `parseDecimal`.
    for (const [name, input] of inputs) values[name] = parseDecimal(input.value);

    for (const input of inputs.values()) input.classList.remove('input-error');

    const result = calc.compute(values);

    if (!result.ok) {
      const { code, params, field } = result.error;
      errorEl.textContent = tKey(locale, code, params);
      errorEl.hidden = false;
      if (field) inputs.get(field)?.classList.add('input-error');
      for (const el of outputs.values()) el.textContent = PLACEHOLDER;
      return;
    }

    errorEl.hidden = true;
    errorEl.textContent = '';

    for (const output of calc.outputs) {
      const el = outputs.get(output.name);
      const value = result.value[output.name];
      if (!el || value === undefined) continue;
      el.textContent = `${format(value, output.precision)} ${tKey(locale, `unit.${output.unit}`)}`;
    }
  };

  // `input` rather than `change` so results track typing, and the form never submits —
  // there is no server to submit to.
  form.addEventListener('input', recompute);
  form.addEventListener('submit', (event) => event.preventDefault());

  /*
   * Double-clicking a field selects all of it.
   *
   * A double-click normally selects the word under the pointer, which means it does nothing
   * at all in the empty space to the right of the digits — and these fields are far wider
   * than the two or three characters they hold, so that empty space is most of the target.
   * Every value here is one short number that gets replaced wholesale, so selecting a
   * "word" was never the useful reading of the gesture.
   */
  form.addEventListener('dblclick', (event) => {
    if (event.target instanceof HTMLInputElement) event.target.select();
  });

  recompute();
}

/**
 * Localised fixed-precision formatter.
 *
 * Precision comes from each output's declared `precision`, never from the raw float:
 * showing 6.666666666666667 litres would imply an accuracy these methods do not have.
 */
function numberFormatter(locale: LocaleCode): (value: number, precision: number) => string {
  const tag = getLocale(locale).numberLocale;
  const cache = new Map<number, Intl.NumberFormat>();

  return (value, precision) => {
    let formatter = cache.get(precision);
    if (!formatter) {
      formatter = new Intl.NumberFormat(tag, {
        minimumFractionDigits: precision,
        maximumFractionDigits: precision,
      });
      cache.set(precision, formatter);
    }
    // Avoid rendering "-0.00" when a correction rounds to nothing.
    return formatter.format(Object.is(value, -0) ? 0 : value);
  };
}

/** Mount every calculator the page contains. */
export function mountAll(locale: LocaleCode, scope: ParentNode = document): void {
  for (const el of scope.querySelectorAll<HTMLElement>('[data-calculator]')) {
    mountCalculator(el, locale);
  }
}
